/**
 * Admin Data Safety tab — platform-admin-only tenant restore UI.
 *
 * Mounted inside src/pages/admin/TenantDetail.tsx as the `data_safety`
 * tab. Provides:
 *   - Table picker (customers / vehicles / appointments)
 *   - Snapshot list (5 fixed intervals within 24h)
 *   - Preview-then-confirm modal flow with required Reason field
 *   - 1-hour undo banner after a successful restore
 *   - Per-tenant security posture card (read-only)
 *
 * Per the Feature spec, restoring tenant data ALSO dispatches an SMS to
 * the tenant owner — the modal previews the SMS body so the operator
 * sees exactly what the tenant will be told.
 */

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { track } from '@/lib/analytics'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { formatDateTime, formatRelative } from '@/lib/dates'

// ─────────────────────────────────────────────────────────────────────
// Wire types — kept narrow + JSON-friendly so the worker contract is
// obvious. Mirror of worker/lib/data-safety.ts public types.

type RestorableTable = 'customers' | 'vehicles' | 'appointments'

const TABLE_LABELS: Record<RestorableTable, string> = {
  customers: 'Customers',
  vehicles: 'Vehicles',
  appointments: 'Appointments',
}

const TABLE_ORDER: RestorableTable[] = ['customers', 'vehicles', 'appointments']

type SnapshotSummary = {
  interval: '5_min' | '30_min' | '2_hour' | '8_hour' | '1_day'
  display: string
  target_at: string
  snapshot: { id: string; snapshot_at: string; row_count: number } | null
  current_row_count: number
  rows_changed: number | null
  rows_added: number | null
  rows_removed: number | null
}

type SnapshotsResponse = {
  tenant: { id: string; name: string }
  table: RestorableTable
  intervals: SnapshotSummary[]
}

type DiffEntry = {
  action: 'remove' | 'revert' | 'reinsert'
  row_id: string
  display_name: string
  current: Record<string, unknown> | null
  snapshot: Record<string, unknown> | null
  changed_fields?: string[]
}

type FkConflict = {
  removed_row_id: string
  removed_row_display: string
  referencing_table: string
  referencing_row_ids: string[]
}

type DiffResponse = {
  diff: {
    table: RestorableTable
    snapshot_id: string
    snapshot_at: string
    entries: DiffEntry[]
    fk_conflicts: FkConflict[]
  }
}

type RestoreResponse = {
  restore_id: string
  audit_log_id: string
  rows_changed: { removed: number; reverted: number; reinserted: number }
  tenant_notification: { sent: boolean; to_phone?: string; error?: string }
}

type PostureItem = {
  key: string
  label: string
  description: string
  status: 'active' | 'inactive' | 'partial'
  value?: string
}

type PostureResponse = {
  tenant: { id: string; name: string }
  items: PostureItem[]
}

// ─────────────────────────────────────────────────────────────────────
// Top-level tab component

export function DataSafetyTab({
  tenantId,
  tenantName,
}: {
  tenantId: string
  tenantName: string
}) {
  const [table, setTable] = useState<RestorableTable>('customers')
  const [pickerSnap, setPickerSnap] = useState<SnapshotSummary | null>(null)
  const [recentRestore, setRecentRestore] = useState<{
    restoreId: string
    rowsChanged: RestoreResponse['rows_changed']
    table: RestorableTable
    interval: SnapshotSummary['interval']
    snapshotAtIso: string
    finishesAt: number // ms epoch
    startedAt: number // ms epoch of the restore (for minutes_after_restore)
  } | null>(null)

  // Fire once per tenant load. Subsequent table-picker changes are
  // already implicit in snapshot_previewed events; this is the entry
  // signal for the surface.
  useEffect(() => {
    track('admin.data_safety.tab_viewed', {
      tenant_id: tenantId,
      actor_role: 'platform_admin',
    })
  }, [tenantId])

  return (
    <div className="space-y-6">
      <OperatorBanner />
      {recentRestore ? (
        <RecentRestoreBanner
          tenantId={tenantId}
          recent={recentRestore}
          onDismiss={() => setRecentRestore(null)}
        />
      ) : null}
      <SnapshotsSection
        tenantId={tenantId}
        table={table}
        onChangeTable={(t) => {
          setTable(t)
          setPickerSnap(null)
        }}
        onPickSnapshot={setPickerSnap}
      />
      <SecurityPostureCardSection tenantId={tenantId} />
      {pickerSnap ? (
        <RestoreDialog
          tenantId={tenantId}
          tenantName={tenantName}
          table={table}
          snapshot={pickerSnap}
          onClose={() => setPickerSnap(null)}
          onSuccess={(result, snapshotAtIso) => {
            const now = Date.now()
            setRecentRestore({
              restoreId: result.restore_id,
              rowsChanged: result.rows_changed,
              table,
              interval: pickerSnap.interval,
              snapshotAtIso,
              finishesAt: now + 60 * 60 * 1000,
              startedAt: now,
            })
            setPickerSnap(null)
          }}
        />
      ) : null}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Operator framing banner — always visible

function OperatorBanner() {
  return (
    <Card className="border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
      <strong>Platform-admin operator action.</strong> Restoring this tenant's
      data is logged in the platform Audit log and notifies the tenant owner
      via SMS after the restore completes. The tenant owner does NOT see this
      tab in their own dashboard.
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Recent-restore banner with Undo button + countdown

function RecentRestoreBanner({
  tenantId,
  recent,
  onDismiss,
}: {
  tenantId: string
  recent: {
    restoreId: string
    rowsChanged: RestoreResponse['rows_changed']
    table: RestorableTable
    interval: SnapshotSummary['interval']
    snapshotAtIso: string
    finishesAt: number
    startedAt: number
  }
  onDismiss: () => void
}) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const [minutesLeft, setMinutesLeft] = useState(() =>
    Math.max(0, Math.ceil((recent.finishesAt - Date.now()) / 60000)),
  )
  useEffect(() => {
    const id = setInterval(() => {
      setMinutesLeft(Math.max(0, Math.ceil((recent.finishesAt - Date.now()) / 60000)))
    }, 30_000)
    return () => clearInterval(id)
  }, [recent.finishesAt])

  const undoMut = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('missing_token')
      const res = await fetch(
        `/api/admin/tenants/${tenantId}/data-safety/undo-restore`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ restore_id: recent.restoreId }),
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `undo_failed_${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      track('admin.data_safety.restore_undone', {
        tenant_id: tenantId,
        table: recent.table,
        interval: recent.interval,
        minutes_after_restore: Math.max(
          0,
          Math.round((Date.now() - recent.startedAt) / 60000),
        ),
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'data-safety', tenantId],
      })
      onDismiss()
    },
  })

  const total =
    recent.rowsChanged.removed +
    recent.rowsChanged.reverted +
    recent.rowsChanged.reinserted

  return (
    <Card className="border-emerald-300 bg-emerald-50 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-sm font-semibold text-emerald-900">
            Restore complete
          </div>
          <div className="mt-0.5 text-sm text-emerald-800">
            {TABLE_LABELS[recent.table]} restored to{' '}
            {formatDateTime(recent.snapshotAtIso)} · {total} row
            {total === 1 ? '' : 's'} changed · Tenant SMS dispatched.
          </div>
          {undoMut.isError ? (
            <div className="mt-2 rounded bg-rose-100 px-2 py-1 text-xs text-rose-800">
              Undo failed:{' '}
              {undoMut.error instanceof Error ? undoMut.error.message : 'unknown'}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={undoMut.isPending}
            onClick={() => undoMut.mutate()}
          >
            {undoMut.isPending
              ? 'Undoing…'
              : `Undo restore (${minutesLeft}m left)`}
          </Button>
          <button
            type="button"
            onClick={onDismiss}
            className="text-[11px] text-emerald-700 hover:underline"
          >
            Dismiss
          </button>
        </div>
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Snapshots section: table picker + interval list

function SnapshotsSection({
  tenantId,
  table,
  onChangeTable,
  onPickSnapshot,
}: {
  tenantId: string
  table: RestorableTable
  onChangeTable: (t: RestorableTable) => void
  onPickSnapshot: (s: SnapshotSummary) => void
}) {
  const { getToken } = useAuth()
  const q = useQuery<SnapshotsResponse>({
    queryKey: ['admin', 'data-safety', tenantId, 'snapshots', table],
    // Anchored to when the operator opens the page; keeps the snapshot
    // list + relative "5 minutes ago" labels fresh while the tab is open.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('missing_token')
      const res = await fetch(
        `/api/admin/tenants/${tenantId}/data-safety/snapshots?table=${table}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) throw new Error(`snapshots_${res.status}`)
      return res.json()
    },
  })

  return (
    <Card className="p-5">
      <div className="mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Restore what (for this tenant)?
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {TABLE_ORDER.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => onChangeTable(t)}
              className={
                t === table
                  ? 'rounded-md border-2 border-brand bg-brand-light px-3 py-2 text-sm font-semibold text-brand-dark'
                  : 'rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground hover:border-brand'
              }
            >
              {TABLE_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Restore to which point in time?
        </div>
        {q.isLoading ? (
          <div className="mt-3 text-sm text-muted-foreground">Loading snapshots…</div>
        ) : q.isError ? (
          <div className="mt-3 rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
            Failed to load snapshots:{' '}
            {q.error instanceof Error ? q.error.message : 'unknown'}
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {q.data!.intervals.map((iv) => (
              <SnapshotRow
                key={iv.interval}
                summary={iv}
                onPick={() => iv.snapshot && onPickSnapshot(iv)}
              />
            ))}
            <div className="pt-2 text-[11px] text-muted-foreground">
              For restores older than 24h, use the 30-day deep archive (ops console).
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

function SnapshotRow({
  summary,
  onPick,
}: {
  summary: SnapshotSummary
  onPick: () => void
}) {
  const hasSnap = !!summary.snapshot
  const isOldest = summary.interval === '1_day'
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={!hasSnap}
      className={
        hasSnap
          ? 'w-full rounded-md border border-border bg-card px-3 py-3 text-left transition hover:border-brand'
          : 'w-full rounded-md border border-border bg-muted px-3 py-3 text-left opacity-60'
      }
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">
            {summary.display}
            {isOldest ? (
              <span className="ml-2 text-[10px] font-normal text-amber-700">
                oldest fast restore
              </span>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            {hasSnap
              ? formatSnapshotRowSubline(summary)
              : `No snapshot yet for this interval (target ${formatRelative(summary.target_at)}).`}
          </div>
        </div>
        {hasSnap ? (
          <span className="text-xs text-muted-foreground">Preview ›</span>
        ) : null}
      </div>
    </button>
  )
}

function formatSnapshotRowSubline(summary: SnapshotSummary): string {
  const snap = summary.snapshot!
  const base = `${formatDateTime(snap.snapshot_at)} · ${snap.row_count} rows`
  const changed = summary.rows_changed ?? 0
  const added = summary.rows_added ?? 0
  const removed = summary.rows_removed ?? 0
  const total = changed + added + removed
  if (total === 0) return `${base} · identical to current`
  const parts: string[] = []
  if (changed > 0) parts.push(`${changed} changed`)
  if (added > 0) parts.push(`+${added} added`)
  if (removed > 0) parts.push(`−${removed} removed`)
  return `${base} · ${parts.join(' · ')}`
}

// ─────────────────────────────────────────────────────────────────────
// Restore dialog: preview diff → confirm with reason + typed phrase

function RestoreDialog({
  tenantId,
  tenantName,
  table,
  snapshot,
  onClose,
  onSuccess,
}: {
  tenantId: string
  tenantName: string
  table: RestorableTable
  snapshot: SnapshotSummary
  onClose: () => void
  onSuccess: (result: RestoreResponse, snapshotAtIso: string) => void
}) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  const previewQ = useQuery<DiffResponse>({
    queryKey: [
      'admin',
      'data-safety',
      tenantId,
      'preview',
      table,
      snapshot.snapshot!.id,
    ],
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('missing_token')
      const res = await fetch(
        `/api/admin/tenants/${tenantId}/data-safety/preview`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            table,
            snapshot_id: snapshot.snapshot!.id,
          }),
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `preview_${res.status}`)
      }
      return res.json()
    },
  })

  // Fire snapshot_previewed once per (snapshot_id) load — not on every
  // re-render of the dialog. The ref guards against the strict-mode
  // double effect call in dev.
  const previewedSnapshotIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!previewQ.data) return
    const snapshotId = previewQ.data.diff.snapshot_id
    if (previewedSnapshotIdRef.current === snapshotId) return
    previewedSnapshotIdRef.current = snapshotId
    track('admin.data_safety.snapshot_previewed', {
      tenant_id: tenantId,
      table,
      interval: snapshot.interval,
      rows_in_diff: previewQ.data.diff.entries.length,
    })
  }, [previewQ.data, tenantId, table, snapshot.interval])

  const [reason, setReason] = useState('')
  const [typedConfirm, setTypedConfirm] = useState('')
  const expectedConfirm = `restore ${table} for ${tenantName}`

  const restoreMut = useMutation({
    mutationFn: async (): Promise<RestoreResponse> => {
      const token = await getToken()
      if (!token) throw new Error('missing_token')
      const res = await fetch(
        `/api/admin/tenants/${tenantId}/data-safety/restore`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            table,
            snapshot_id: snapshot.snapshot!.id,
            reason: reason.trim(),
            confirmation: typedConfirm.trim(),
          }),
        },
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error ?? `restore_${res.status}`)
      }
      return res.json()
    },
    onSuccess: (result) => {
      const total =
        result.rows_changed.removed +
        result.rows_changed.reverted +
        result.rows_changed.reinserted
      track('admin.data_safety.restore_executed', {
        tenant_id: tenantId,
        table,
        interval: snapshot.interval,
        rows_changed: total,
        reason_length: reason.trim().length,
        tenant_sms_sent: result.tenant_notification.sent,
      })
      queryClient.invalidateQueries({
        queryKey: ['admin', 'data-safety', tenantId],
      })
      onSuccess(result, snapshot.snapshot!.snapshot_at)
    },
  })

  const diff = previewQ.data?.diff
  const blockingConflicts = diff?.fk_conflicts ?? []
  const canRestore =
    !!diff &&
    blockingConflicts.length === 0 &&
    reason.trim().length > 0 &&
    typedConfirm.trim() === expectedConfirm &&
    !restoreMut.isPending

  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Restore {TABLE_LABELS[table]} for {tenantName} — to {snapshot.display}
          </DialogTitle>
          <DialogDescription>
            Preview the diff, then confirm with a reason and the typed phrase.
            Tenant owner will receive an SMS describing the change.
          </DialogDescription>
        </DialogHeader>

        {previewQ.isLoading ? (
          <div className="py-6 text-sm text-muted-foreground">
            Computing diff…
          </div>
        ) : previewQ.isError ? (
          <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
            Preview failed:{' '}
            {previewQ.error instanceof Error ? previewQ.error.message : 'unknown'}
          </div>
        ) : (
          <>
            <DiffSummary diff={diff!} />
            {blockingConflicts.length > 0 ? (
              <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
                <div className="font-semibold">
                  Cannot restore — {blockingConflicts.length} foreign-key
                  conflict{blockingConflicts.length === 1 ? '' : 's'}
                </div>
                <ul className="mt-1 list-disc pl-5">
                  {blockingConflicts.map((c, i) => (
                    <li key={i}>
                      <span className="font-medium">{c.removed_row_display}</span>{' '}
                      is referenced by {c.referencing_row_ids.length} row
                      {c.referencing_row_ids.length === 1 ? '' : 's'} in{' '}
                      {c.referencing_table}.
                    </li>
                  ))}
                </ul>
                <div className="mt-1 text-xs">
                  Pick a different snapshot, or resolve the dependent rows in
                  the tenant first.
                </div>
              </div>
            ) : (
              <TenantSmsPreview
                table={table}
                snapshotAtIso={snapshot.snapshot!.snapshot_at}
                reason={reason}
                rowsChanged={diff!.entries.length}
              />
            )}

            <div className="space-y-3">
              <div>
                <Label htmlFor="reason-input">
                  Reason (shown in tenant SMS + audit log)
                </Label>
                <Input
                  id="reason-input"
                  placeholder="e.g. bad-import-recovery"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={200}
                />
              </div>
              <div>
                <Label htmlFor="confirm-input">
                  Type{' '}
                  <code className="rounded bg-amber-100 px-1 font-mono text-amber-900">
                    {expectedConfirm}
                  </code>{' '}
                  to confirm
                </Label>
                <Input
                  id="confirm-input"
                  placeholder={expectedConfirm}
                  value={typedConfirm}
                  onChange={(e) => setTypedConfirm(e.target.value)}
                  className="font-mono"
                />
              </div>
              {restoreMut.isError ? (
                <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
                  Restore failed:{' '}
                  {restoreMut.error instanceof Error
                    ? restoreMut.error.message
                    : 'unknown'}
                </div>
              ) : null}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!canRestore}
            onClick={() => restoreMut.mutate()}
          >
            {restoreMut.isPending ? 'Restoring…' : 'Restore tenant data'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DiffSummary({ diff }: { diff: DiffResponse['diff'] }) {
  const removed = diff.entries.filter((e) => e.action === 'remove').length
  const reverted = diff.entries.filter((e) => e.action === 'revert').length
  const reinserted = diff.entries.filter((e) => e.action === 'reinsert').length
  return (
    <div>
      <div className="text-sm font-semibold text-foreground">
        {diff.entries.length} row{diff.entries.length === 1 ? '' : 's'} will
        change
      </div>
      <div className="mt-1 flex flex-wrap gap-2 text-xs">
        <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">
          {removed} remove
        </Badge>
        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">
          {reverted} revert
        </Badge>
        <Badge variant="outline" className="border-blue-300 bg-blue-50 text-blue-700">
          {reinserted} re-insert
        </Badge>
      </div>
      {diff.entries.length > 0 ? (
        <div className="mt-3 max-h-48 overflow-y-auto rounded border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">Action</th>
                <th className="px-2 py-1.5 text-left">Row</th>
                <th className="px-2 py-1.5 text-left">Detail</th>
              </tr>
            </thead>
            <tbody>
              {diff.entries.slice(0, 50).map((e, i) => (
                <tr key={i} className="border-t border-border/40">
                  <td className="px-2 py-1.5">
                    <span
                      className={
                        e.action === 'remove'
                          ? 'rounded bg-rose-100 px-1.5 py-0.5 text-rose-700'
                          : e.action === 'revert'
                            ? 'rounded bg-amber-100 px-1.5 py-0.5 text-amber-700'
                            : 'rounded bg-blue-100 px-1.5 py-0.5 text-blue-700'
                      }
                    >
                      {e.action}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 font-medium">{e.display_name}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">
                    {e.action === 'revert'
                      ? `Fields: ${e.changed_fields?.join(', ') ?? ''}`
                      : e.action === 'remove'
                        ? 'Entire row removed'
                        : 'Row re-inserted from snapshot'}
                  </td>
                </tr>
              ))}
              {diff.entries.length > 50 ? (
                <tr>
                  <td colSpan={3} className="px-2 py-1.5 text-center text-muted-foreground">
                    …+{diff.entries.length - 50} more
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}

function TenantSmsPreview({
  table,
  snapshotAtIso,
  reason,
  rowsChanged,
}: {
  table: RestorableTable
  snapshotAtIso: string
  reason: string
  rowsChanged: number
}) {
  const snapPretty = formatDateTime(snapshotAtIso)
  const bodyReason = reason.trim() || '(reason will be required)'
  const body = `Autoflo support restored your ${table} data to ${snapPretty}. ${rowsChanged} row${rowsChanged === 1 ? '' : 's'} changed. Reason: '${bodyReason}'. Reply HELP for support.`
  return (
    <div className="rounded border border-border bg-muted/30 p-3 text-xs">
      <div className="mb-1 font-semibold text-foreground">
        Tenant owner SMS preview
      </div>
      <div className="rounded bg-card p-2 font-mono text-foreground">{body}</div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Security posture card

function SecurityPostureCardSection({ tenantId }: { tenantId: string }) {
  const { getToken } = useAuth()
  useEffect(() => {
    track('admin.data_safety.security_posture_viewed', {
      tenant_id: tenantId,
    })
  }, [tenantId])
  const q = useQuery<PostureResponse>({
    queryKey: ['admin', 'data-safety', tenantId, 'posture'],
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('missing_token')
      const res = await fetch(
        `/api/admin/tenants/${tenantId}/data-safety/security-posture`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) throw new Error(`posture_${res.status}`)
      return res.json()
    },
  })
  return (
    <Card className="p-5">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Security posture
      </div>
      <div className="mb-3 text-sm text-foreground">
        How this tenant's data is protected at rest, in transit, and via backups.
      </div>
      {q.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : q.isError ? (
        <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          Failed to load posture
        </div>
      ) : (
        <div className="divide-y divide-border">
          {q.data!.items.map((it) => (
            <div
              key={it.key}
              className="flex items-start justify-between gap-3 py-2.5"
            >
              <div className="flex-1">
                <div className="text-sm font-medium text-foreground">
                  {it.label}
                </div>
                <div className="text-xs text-muted-foreground">
                  {it.description}
                </div>
              </div>
              <div>
                {it.status === 'active' ? (
                  <Badge className="border-emerald-300 bg-emerald-100 text-emerald-800">
                    {it.value ? `✓ ${it.value}` : '✓ active'}
                  </Badge>
                ) : it.status === 'inactive' ? (
                  <Badge className="border-rose-300 bg-rose-100 text-rose-800">
                    not configured
                  </Badge>
                ) : (
                  <Badge className="border-amber-300 bg-amber-100 text-amber-800">
                    partial
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
